import {ActionEvent, Controller} from '@hotwired/stimulus';
import {createHTMLElementFromTemplate} from 'app/utils/dom';
import {throttle} from "throttle-debounce";
import {Typed} from "app/stimulus/typed-stimulus";
import {AsStimulusController} from "app/stimulus/as-stimulus-controller";
import {onLiveComponentRender} from "app/stimulus/live-component-render";
import HorizontalOverflowScroller, {isOverflowSide, OverflowSide} from "app/utils/horizontal-overflow-scroller";
import InvalidArgumentException from "app/errors/invalid-argument-exception";
import InvalidStateException from "app/errors/invalid-state-exception";
import DropdownController from "components/Dropdown/Dropdown/dropdown-controller";

/** Marks the menu of a "⋯" dropdown that receives the copies of the hidden tabs. */
const OVERFLOW_MENU_ATTRIBUTE = 'data-tabs-overflow-menu';

/**
 * Tabs that do not fit their width switch to the compact layout first and, when that is not enough, to a
 * horizontal scroll strip with an arrow and a "⋯" menu of the hidden tabs on each side. A menu item never runs
 * an action of its own: it clicks the real tab, so the tab's Live action, loader or panel switching run as on
 * a normal click. The selected tab is kept in view on connect, on width changes and after a Live render.
 * The tablist uses a roving tabindex: the arrow keys and Home/End only move the focus, Enter/Space activate the
 * focused tab natively (manual activation, a Live tab sends a request).
 */
@AsStimulusController('tabs')
export default class extends Typed(
    Controller<HTMLElement>, {
        targets: {
            tabsContainer: HTMLElement,
            scroller: HTMLElement,
            tabItemContainer: HTMLElement,
            tabItem: HTMLElement,
            tabItemTemplate: HTMLTemplateElement,
            contentItem: HTMLElement,
            startControl: HTMLButtonElement,
            endControl: HTMLButtonElement,
        },
        classes: [
            'active',
            'compact',
            'overflow',
            'canScrollStart',
            'canScrollEnd',
        ] as const,
    }
)
{
    private tabsContainerResizeObserver!: ResizeObserver;
    private scrollerMutationObserver!: MutationObserver;
    private overflowScroller: HorizontalOverflowScroller | null = null;
    private removeRenderListener: (() => void) | null = null;
    private rovingSyncFrame: number | null = null;
    private selectedIndex: number = -1;
    private tabsContainerWidth: number | null = null;
    private tabsContainerWidthChanged: boolean = false;
    private selectedTabVisible: boolean = true;

    public initialize(): void {
        this.resize = throttle(100, this.resize.bind(this));
        this.tabsContainerResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]): void => {
            // Switching the compact layout changes only the height, which must not trigger another measurement.
            const width = entries[entries.length - 1].contentRect.width;
            if (width === this.tabsContainerWidth) {
                return;
            }
            this.tabsContainerWidth = width;
            this.tabsContainerWidthChanged = true;
            this.resize();
        });
        this.scrollerMutationObserver = new MutationObserver((): void => this.resize());
    }

    public connect(): void {
        this.overflowScroller = new HorizontalOverflowScroller(
            this.scrollerTarget,
            (): HTMLElement[] => this.tabItemContainerTargets,
            (): void => this.updateScrollState(),
        );
        this.overflowScroller.attach();
        this.syncSelectedIndex();
        this.tabsContainerResizeObserver.observe(this.tabsContainerTarget);
        this.scrollerMutationObserver.observe(this.scrollerTarget, {childList: true, subtree: true, characterData: true});
        this.updateRovingTabIndex(null);
        this.measure();
        this.scrollSelectedTabIntoView('auto');
        this.removeRenderListener = onLiveComponentRender(this.element, (): void => this.onRender());
        document.fonts.ready.then((): void => {
            if (this.overflowScroller !== null) {
                this.resize();
            }
        });
    }

    public disconnect(): void {
        this.removeRenderListener?.();
        this.removeRenderListener = null;
        if (this.rovingSyncFrame !== null) {
            cancelAnimationFrame(this.rovingSyncFrame);
            this.rovingSyncFrame = null;
        }
        this.tabsContainerResizeObserver.disconnect();
        this.scrollerMutationObserver.disconnect();
        this.overflowScroller?.detach();
        this.overflowScroller = null;
        this.tabsContainerWidth = null;
    }

    public resize(): void {
        this.measure();
    }

    public page(event: ActionEvent): void {
        void this.getOverflowScroller().page(this.getSideParam(event));
    }

    /**
     * Fills a freshly created "⋯" dropdown with copies of the tabs hidden on its side.
     */
    public fillOverflowDropdown(event: ActionEvent & CustomEvent<{dropdown: HTMLElement}>): void {
        const side = this.getSideParam(event);
        const toggler = event.currentTarget;
        const dropdown = event.detail.dropdown;
        const menu = dropdown.querySelector<HTMLElement>(`[${OVERFLOW_MENU_ATTRIBUTE}]`);
        if (menu === null || !(toggler instanceof HTMLElement)) {
            return;
        }
        const menuItems = new Map<HTMLElement, number>();
        for (const tabItemContainer of this.getOverflowScroller().getHiddenItems(side)) {
            const index = this.tabItemContainerTargets.indexOf(tabItemContainer);
            const menuItem = createHTMLElementFromTemplate(this.tabItemTemplateTargets[index]);
            const active = index === this.selectedIndex;
            menuItem.classList.toggle(this.activeClass, active);
            if (active) {
                menuItem.setAttribute('aria-current', 'true');
            } else {
                menuItem.removeAttribute('aria-current');
            }
            menuItems.set(menuItem, index);
            menu.appendChild(menuItem);
        }
        // Captured on the dropdown so the menu item's own handling never runs; the real tab gets clicked instead.
        dropdown.addEventListener('click', (clickEvent: MouseEvent): void => {
            if (!(clickEvent.target instanceof Node)) {
                return;
            }
            for (const [menuItem, index] of menuItems) {
                if (!menuItem.contains(clickEvent.target)) {
                    continue;
                }
                clickEvent.preventDefault();
                clickEvent.stopPropagation();
                void this.activateTab(index, DropdownController.get(toggler));
                return;
            }
        }, {capture: true});
    }

    /**
     * Client-side switching of tabs with a `targetElement`.
     */
    public selectTab(event: ActionEvent): void {
        const targetId = event.params.target;
        for (let i = 0; i < this.tabItemTargets.length; i++) {
            const tabItem = this.tabItemTargets[i];
            const selected = tabItem.dataset.tabsTargetElement === targetId;
            tabItem.classList.toggle(this.activeClass, selected);
            tabItem.setAttribute('aria-selected', selected ? 'true' : 'false');
            if (selected) {
                this.selectedIndex = i;
            }
        }
        for (let i = 0; i < this.contentItemTargets.length; i++) {
            const contentItem = this.contentItemTargets[i];
            contentItem.style.display = contentItem.id === targetId ? '' : 'none';
        }
        this.updateRovingTabIndex(null);
        this.scrollSelectedTabIntoView('smooth');
    }

    /**
     * Arrow keys (with wrap-around) and Home/End move the focus between the enabled tabs.
     */
    public navigate(event: KeyboardEvent): void {
        if (event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }
        const tabItems = this.getEnabledTabItems();
        const position = tabItems.findIndex((tabItem: HTMLElement): boolean => tabItem === event.target);
        if (position < 0) {
            return;
        }
        let next: HTMLElement;
        switch (event.key) {
            case 'ArrowRight':
                next = tabItems[(position + 1) % tabItems.length];
                break;
            case 'ArrowLeft':
                next = tabItems[(position - 1 + tabItems.length) % tabItems.length];
                break;
            case 'Home':
                next = tabItems[0];
                break;
            case 'End':
                next = tabItems[tabItems.length - 1];
                break;
            default:
                return;
        }
        event.preventDefault();
        this.focusTab(next);
    }

    /**
     * Focus leaving the tablist hands the tab stop back to the selected tab.
     */
    public resetRovingTabIndex(event: FocusEvent): void {
        if (event.relatedTarget instanceof Node && this.scrollerTarget.contains(event.relatedTarget)) {
            return;
        }
        this.updateRovingTabIndex(null);
    }

    private onRender(): void {
        const previousIndex = this.selectedIndex;
        this.syncSelectedIndex();
        this.syncRovingTabIndexAfterRender();
        this.measure();
        if (previousIndex !== this.selectedIndex) {
            this.scrollSelectedTabIntoView('smooth');
        }
    }

    /**
     * The morph puts the server markup (the selected tab has `tabindex="0"`) back and re-applies the tabindex
     * changes recorded by Live on top of it, so a tab stop moved earlier by the arrow keys can land on a stale tab.
     * The tab stop is set again from the rendered state: the focused tab keeps it, otherwise the selected one.
     * It is repeated in the next frame, after the Live loading state and the mutation callbacks of the render
     * have settled, so a later change made during the same render does not leave the stop on another tab.
     */
    private syncRovingTabIndexAfterRender(): void {
        this.updateRovingTabIndex(this.getFocusedTabItem());
        if (this.rovingSyncFrame !== null) {
            cancelAnimationFrame(this.rovingSyncFrame);
        }
        this.rovingSyncFrame = requestAnimationFrame((): void => {
            this.rovingSyncFrame = null;
            if (this.overflowScroller === null) {
                return;
            }
            this.syncSelectedIndex();
            this.updateRovingTabIndex(this.getFocusedTabItem());
        });
    }

    private getFocusedTabItem(): HTMLElement | null {
        const focused = document.activeElement;
        if (!(focused instanceof HTMLElement)) {
            return null;
        }
        return this.tabItemTargets.includes(focused) ? focused : null;
    }

    private measure(): void {
        if (this.overflowScroller === null) {
            return;
        }
        const wasOverflowing = this.isOverflowing();
        // A width change keeps the selected tab in view, but only if the user has not scrolled it away.
        const keepSelectedTabVisible = this.tabsContainerWidthChanged && (!wasOverflowing || this.selectedTabVisible);
        this.tabsContainerWidthChanged = false;
        // Measuring drops the scroll container for a moment, which would reset the scroll position.
        const scrollLeft = this.scrollerTarget.scrollLeft;
        this.element.classList.remove(this.compactClass, this.overflowClass);
        if (this.tabItemContainerTargets.length > 1 && this.overflowScroller.hasOverflow()) {
            this.element.classList.add(this.compactClass);
            if (this.overflowScroller.hasOverflow()) {
                this.element.classList.add(this.overflowClass);
            }
        }
        if (!this.isOverflowing()) {
            this.updateScrollState();
            return;
        }
        this.scrollerTarget.scrollLeft = scrollLeft;
        this.updateScrollState();
        if (!wasOverflowing || keepSelectedTabVisible) {
            this.scrollSelectedTabIntoView('auto');
        }
    }

    private async activateTab(index: number, dropdown: DropdownController | null): Promise<void> {
        const tabItemContainer = this.tabItemContainerTargets[index];
        await Promise.all([
            dropdown?.close(),
            this.getOverflowScroller().scrollItemIntoView(tabItemContainer, 'smooth'),
        ]);
        const tabItem = this.tabItemTargets[index];
        // The focus moves from the closed menu to the real tab, which is already scrolled into view.
        this.updateRovingTabIndex(tabItem);
        tabItem.focus({preventScroll: true});
        tabItem.click();
    }

    private focusTab(tabItem: HTMLElement): void {
        this.updateRovingTabIndex(tabItem);
        tabItem.focus({preventScroll: true});
        const tabItemContainer = this.tabItemContainerTargets[this.tabItemTargets.indexOf(tabItem)];
        if (this.overflowScroller !== null && this.isOverflowing() && tabItemContainer !== undefined) {
            void this.overflowScroller.scrollItemIntoView(tabItemContainer, 'smooth');
        }
    }

    /**
     * Keeps exactly one tab in the Tab order: the given one, otherwise the selected one, otherwise the first
     * enabled one. Live keeps the attributes set here across a render, so they are set again after each render.
     */
    private updateRovingTabIndex(focusedTabItem: HTMLElement | null): void {
        const tabItems = this.getEnabledTabItems();
        const selectedTabItem = this.tabItemTargets[this.selectedIndex];
        let rovingTabItem: HTMLElement | null = focusedTabItem;
        if (rovingTabItem === null && selectedTabItem !== undefined && tabItems.includes(selectedTabItem)) {
            rovingTabItem = selectedTabItem;
        }
        if (rovingTabItem === null) {
            rovingTabItem = tabItems[0] ?? null;
        }
        for (const tabItem of this.tabItemTargets) {
            tabItem.tabIndex = tabItem === rovingTabItem ? 0 : -1;
        }
    }

    private getEnabledTabItems(): HTMLElement[] {
        return this.tabItemTargets.filter((tabItem: HTMLElement): boolean => {
            return !tabItem.hasAttribute('disabled') && tabItem.getAttribute('aria-disabled') !== 'true';
        });
    }

    private updateScrollState(): void {
        if (this.overflowScroller === null) {
            return;
        }
        const overflowing = this.isOverflowing();
        const canScrollStart = overflowing && this.overflowScroller.canScroll('start');
        const canScrollEnd = overflowing && this.overflowScroller.canScroll('end');
        this.element.classList.toggle(this.canScrollStartClass, canScrollStart);
        this.element.classList.toggle(this.canScrollEndClass, canScrollEnd);
        for (const control of this.startControlTargets) {
            control.disabled = !canScrollStart;
        }
        for (const control of this.endControlTargets) {
            control.disabled = !canScrollEnd;
        }
        this.selectedTabVisible = !overflowing || this.isSelectedTabVisible();
    }

    private scrollSelectedTabIntoView(behavior: ScrollBehavior): void {
        if (this.overflowScroller === null || !this.isOverflowing() || this.selectedIndex < 0) {
            return;
        }
        const tabItemContainer = this.tabItemContainerTargets[this.selectedIndex];
        if (tabItemContainer === undefined) {
            return;
        }
        this.selectedTabVisible = true;
        void this.overflowScroller.scrollItemIntoView(tabItemContainer, behavior);
    }

    private isSelectedTabVisible(): boolean {
        const tabItemContainer = this.tabItemContainerTargets[this.selectedIndex];
        return tabItemContainer !== undefined && this.getOverflowScroller().isItemVisible(tabItemContainer);
    }

    private isOverflowing(): boolean {
        return this.element.classList.contains(this.overflowClass);
    }

    private syncSelectedIndex(): void {
        this.selectedIndex = this.tabItemTargets.findIndex(
            (tabItem: HTMLElement): boolean => tabItem.classList.contains(this.activeClass),
        );
    }

    private getOverflowScroller(): HorizontalOverflowScroller {
        if (this.overflowScroller === null) {
            throw new InvalidStateException('The tabs controller is not connected.');
        }
        return this.overflowScroller;
    }

    private getSideParam(event: ActionEvent): OverflowSide {
        const side: unknown = event.params.side;
        if (!isOverflowSide(side)) {
            throw new InvalidArgumentException(`Unknown overflow side "${String(side)}".`);
        }
        return side;
    }
}
